// /inbox — tenant workspace: the agent inbox (mockups 21/22). Composes the
// design-system inbox pieces into the 3-column layout (list · thread · contact).
// reply/note, return-to-bot and resolve update local state optimistically and
// call the B-L2 endpoints (replyConversation / returnConversationToBot /
// resolveConversation), refetching the list on success. Data via the
// listConversations() seam -> GET /conversations.

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  type Conversation,
  listConversations,
  replyConversation,
  resolveConversation,
  returnConversationToBot,
  type ThreadItem,
} from './inbox-data.js'
import { useAuthStore } from '../../auth/store.js'
import {
  Button,
  Composer,
  FormField,
  Modal,
  Select,
  ConversationCard,
  ConversationList,
  DaySeparator,
  EmptyStateCard,
  InboxContactPanel,
  InternalNote,
  MessageBubble,
  MessageThread,
  ServiceWindowBanner,
  Spinner,
  SystemEvent,
  ThreeColumnLayout,
  toast,
} from '../../design-system/index.js'
import { assignConversation, returnToPool } from '../oversight/oversight-data.js'
import { useAssignableMembers } from '../oversight/useAssignableMembers.js'

type Filter = 'todas' | 'contigo' | 'sensibles'

/** The design-system empty-state slot expects an SVG (`[&_svg]:size-[26px]`);
 *  it held a raw 💬 glyph, which the sizing rule never touched, so the card's
 *  icon rendered at whatever the font felt like. Same speech-bubble path the
 *  timeline and the "Ver la conversación" link already use. */
function ChatIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </svg>
  )
}

/** What an EMPTY TAB says. Each one answers a different question, which is the
 *  whole point: "nobody has assigned you anything" and "this workspace has never
 *  had a conversation" were the same screen before. The `todas` entry keeps the
 *  Record total — an empty default tab is the day-one card above, not this. */
const EMPTY_TAB: Record<Filter, { title: string; body: string }> = {
  todas: {
    title: 'Sin conversaciones por ahora',
    body: 'Cuando llegue un mensaje nuevo lo verás aquí.',
  },
  contigo: {
    title: 'Sin conversaciones contigo por ahora',
    body: 'Las conversaciones que tomes aparecerán aquí.',
  },
  sensibles: {
    title: 'Sin conversaciones sensibles por ahora',
    body: 'Aquí aparecen las que tocan un tema delicado y conviene atender primero.',
  },
}

function ThreadItemView({ item }: { item: ThreadItem }): JSX.Element {
  switch (item.type) {
    case 'day':
      return <DaySeparator label={item.label} />
    case 'event':
      return <SystemEvent kind={item.kind} text={item.text} />
    case 'note':
      return (
        <InternalNote author={item.author} time={item.time}>
          {item.text}
        </InternalNote>
      )
    case 'msg':
      return (
        <MessageBubble
          sender={item.sender}
          time={item.time}
          {...(item.author ? { author: item.author } : {})}
        >
          {item.text}
        </MessageBubble>
      )
  }
}

export function InboxPage(): JSX.Element {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<Filter>('todas')
  // "Contigo" is asked of the SERVER (a SQL predicate), not of the 50 rows we
  // happen to be holding: filtering here hid your own older conversations as
  // soon as the tenant had more than one page of traffic. "Sensibles" stays
  // client-side — it reads a flag already on every row we render.
  const assignedToMe = filter === 'contigo'
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['inbox', 'conversations', assignedToMe ? 'mias' : 'todas'],
    queryFn: () => listConversations(assignedToMe),
    // The inbox is the one screen an Asesor SITS ON waiting for customers.
    // Without polling it rendered live data exactly once: `refetchOnWindowFocus`
    // is globally disabled (main.tsx), so an incoming message never appeared
    // until the agent navigated away and back or hard-reloaded. Own replies
    // showed up only because mutations invalidate this query.
    // 15s: tighter than Oversight/AppLayout (30s) because a waiting customer is
    // the most time-sensitive thing in the product, looser than Concierge (5s)
    // because this list is heavier.
    refetchInterval: 15_000,
    // Polling alone isn't enough — a backgrounded tab stops firing intervals in
    // some browsers, and an agent tabbing back must not see a stale queue.
    refetchOnWindowFocus: true,
    // "Contigo" is a different queryKey, so without this `data` goes undefined
    // for the length of the round trip and the page falls through every guard
    // below. Same fix class as the board's F16: switching tabs is a repaint of
    // the queue, not a teardown of the screen.
    placeholderData: keepPreviousData,
  })
  const [convos, setConvos] = useState<Conversation[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const [mode, setMode] = useState<'reply' | 'note'>('reply')
  // Deep-link target (e.g. from a notification's "Ver conversación"): /inbox?c=<id>.
  const [searchParams] = useSearchParams()
  const targetId = searchParams.get('c')

  useEffect(() => {
    if (data) {
      setConvos(data)
      // Prefer the deep-link target (if it's in the list), else keep the current
      // selection, else default to the first conversation.
      // Keep the current selection only while it is still IN the list: the tab
      // is a server query now, so switching to "Contigo" can drop the
      // conversation that was open and a dangling id would render an empty
      // thread beside a full list.
      setActiveId((cur) => {
        if (cur && data.some((c) => c.id === cur)) return cur
        if (targetId && data.some((c) => c.id === targetId)) return targetId
        return data[0]?.id ?? ''
      })
    }
  }, [data, targetId])

  const visible = useMemo(
    () => convos.filter((c) => (filter === 'sensibles' ? c.priority === 'sens' : true)),
    [convos, filter],
  )

  const active = convos.find((c) => c.id === activeId) ?? null

  function select(id: string): void {
    setActiveId(id)
    setConvos((cs) => cs.map((c) => (c.id === id ? { ...c, unread: false } : c)))
  }

  // Apply an optimistic patch, run the write, and refetch on success. On failure
  // re-sync from the server (drops the optimistic change) and surface the error.
  //
  // Cancel any in-flight fetch first: without this, a stale refetch already in
  // flight could resolve right after the click and `setConvos(data)` would
  // overwrite the optimistic change with pre-change data — making the action look
  // like it did nothing until a second click (the return-to-bot "two clicks" bug).
  function mutate(
    id: string,
    optimistic: (c: Conversation) => Conversation,
    run: () => Promise<void>,
    failMsg: string,
  ): void {
    void qc.cancelQueries({ queryKey: ['inbox', 'conversations'] })
    setConvos((cs) => cs.map((c) => (c.id === id ? optimistic(c) : c)))
    run()
      .then(() => qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] }))
      .catch(() => {
        toast.error('No se pudo completar', { description: failMsg })
        void qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] })
      })
  }

  function onSend(text: string, sendMode: 'reply' | 'note'): void {
    if (!active) return
    const item: ThreadItem =
      sendMode === 'note'
        ? { type: 'note', id: `n-${text.length}-${Date.now()}`, author: 'Tú', time: 'ahora', text }
        : {
            type: 'msg',
            id: `m-${text.length}-${Date.now()}`,
            sender: 'agent',
            author: 'Tú',
            time: 'ahora',
            text,
          }
    mutate(
      active.id,
      (c) => ({
        ...c,
        thread: [...c.thread, item],
        snippet: text,
        assignment: sendMode === 'note' ? c.assignment : 'you',
      }),
      () => replyConversation(active.id, text, sendMode),
      sendMode === 'note' ? 'No se pudo guardar la nota.' : 'No se pudo enviar el mensaje.',
    )
  }

  function onReturnToBot(): void {
    if (!active) return
    mutate(
      active.id,
      (c) => ({
        ...c,
        assignment: 'bot',
        holder: null,
        thread: [
          ...c.thread,
          { type: 'event', id: `e-${Date.now()}`, kind: 'reopened', text: 'Devuelta al agente' },
        ],
      }),
      () => returnConversationToBot(active.id),
      'No se pudo devolver al agente.',
    )
  }

  const ownUserId = useAuthStore((st) => st.user?.id ?? null)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [reassignTo, setReassignTo] = useState('')
  // Fetched with the page, not on modal open: the button itself depends on
  // whether there is anyone else to hand the conversation to.
  const { members: assignables, canReassign } = useAssignableMembers()
  // CP-1 F-C: "Reasignar" presupone una asignación previa. La hay cuando la
  // conversación es TUYA o de una compañera ('other'); con el Agente ('bot') o
  // en la cola ('unassigned') nadie la ha tomado, y el botón se llama Asignar.
  const held = active?.assignment === 'you' || active?.assignment === 'other'
  const assignVerb = held ? 'Reasignar' : 'Asignar'
  // Y la línea de estado dice la verdad de los cuatro casos. Decía "En espera
  // de tu respuesta" incluso cuando la conversación era de alguien más.
  const stateLine =
    active?.assignment === 'bot'
      ? 'Atendida por el agente'
      : active?.assignment === 'other'
        ? `Con ${active.holder ?? 'otro asesor'}`
        : 'En espera de tu respuesta'

  // C5: the actions Supervisión had and Bandeja lacked. Same optimistic
  // mutate() convention as the neighbours.
  function onTake(): void {
    if (!active || !ownUserId) return
    mutate(
      active.id,
      (c) => ({ ...c, assignment: 'you', holder: null }),
      () => assignConversation(active.id, ownUserId),
      'No se pudo tomar la conversación.',
    )
  }

  function onReturnToPool(): void {
    if (!active) return
    mutate(
      active.id,
      (c) => ({
        ...c,
        assignment: 'unassigned',
        holder: null,
        thread: [
          ...c.thread,
          { type: 'event', id: `e-${Date.now()}`, kind: 'reopened', text: 'Devuelta a la cola' },
        ],
      }),
      () => returnToPool(active.id),
      'No se pudo devolver a la cola.',
    )
  }

  function onReassign(): void {
    if (!active || !reassignTo) return
    setReassignOpen(false)
    // El estado optimista es el REAL: la conversación queda de la persona
    // elegida, no "sin asignar". Marcarla como libre la dejaba, por un
    // instante, ofreciendo "Tomar conversación" sobre el hilo que se acababa
    // de entregar.
    const to = assignables.find((m) => m.id === reassignTo)
    mutate(
      active.id,
      (c) => ({
        ...c,
        ...(reassignTo === ownUserId
          ? { assignment: 'you' as const, holder: to?.name ?? null }
          : { assignment: 'other' as const, holder: to?.name ?? null }),
      }),
      () => assignConversation(active.id, reassignTo),
      'No se pudo reasignar.',
    )
  }

  function onResolve(): void {
    if (!active) return
    mutate(
      active.id,
      (c) => ({
        ...c,
        assignment: 'unassigned',
        holder: null,
        thread: [
          ...c.thread,
          { type: 'event', id: `e-${Date.now()}`, kind: 'resolved', text: 'Conversación resuelta' },
        ],
      }),
      () => resolveConversation(active.id),
      'No se pudo resolver la conversación.',
    )
  }

  const tabs = (
    <div role="tablist" aria-label="Filtro de conversaciones" className="flex gap-1">
      {(
        [
          ['todas', 'Todas'],
          ['contigo', 'Contigo'],
          ['sensibles', 'Sensibles'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={filter === id}
          onClick={() => setFilter(id)}
          className={
            filter === id
              ? 'rounded-md bg-field px-2.5 py-1 text-[11px] font-semibold text-text'
              : 'rounded-md px-2.5 py-1 text-[11px] font-semibold text-text-2 hover:text-text'
          }
        >
          {label}
        </button>
      ))}
    </div>
  )

  // DAY ONE AND A FILTERED-EMPTY ANSWER ARE DIFFERENT STATES.
  //
  // There used to be one whole-page early return for `convos.length === 0`, and
  // the tab strip is built inside the layout it returned instead of. So an agent
  // who clicked "Contigo" with nothing assigned to them lost the tabs along with
  // the list — no Todas to click, no way back except the browser's Back button
  // or a reload. The full-page take-over now survives ONLY for the two states
  // that really are about the whole screen: the first load, and a workspace that
  // has never had a conversation at all.
  if (isLoading && convos.length === 0) {
    return (
      <div data-testid="inbox" className="flex h-full items-center justify-center">
        <Spinner label="Cargando conversaciones…" />
      </div>
    )
  }
  // True day one: the DEFAULT tab, answered, and empty. A "Contigo" that came
  // back empty says nothing about the workspace, so it must not borrow this card.
  if (filter === 'todas' && !isError && convos.length === 0) {
    return (
      <div data-testid="inbox" className="flex h-full items-center justify-center p-6">
        <EmptyStateCard
          icon={<ChatIcon />}
          kind="day1"
          title="Sin conversaciones por ahora"
          body="Cuando un cliente escriba y el agente pase la conversación a una persona, aparecerá aquí."
        />
      </div>
    )
  }

  // Everything else renders INSIDE the layout, so the tabs are always reachable:
  // an empty tab, and even a failed fetch, are answers about the list column.
  // A polling error while we still hold a queue stays silent — blanking a
  // waiting customer off the screen is worse than an unreported refetch.
  const listBody =
    isError && convos.length === 0 ? (
      <li className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <p className="text-[12.5px] text-text-2">
          No pudimos cargar la bandeja. Puede haber conversaciones esperando.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </li>
    ) : visible.length === 0 ? (
      <li className="px-3 py-6">
        <EmptyStateCard
          icon={<ChatIcon />}
          kind="no-results"
          headingLevel={3}
          title={EMPTY_TAB[filter].title}
          body={EMPTY_TAB[filter].body}
          className="px-4 py-6"
        />
      </li>
    ) : (
      visible.map((c) => (
        <ConversationCard
          key={c.id}
          contact={c.contact}
          snippet={c.snippet}
          time={c.time}
          unread={c.unread}
          active={c.id === activeId}
          assignment={c.assignment}
          {...(c.holder ? { holderName: c.holder } : {})}
          {...(c.priority ? { priority: c.priority } : {})}
          onClick={() => select(c.id)}
        />
      ))
    )

  return (
    <div data-testid="inbox" className="h-full overflow-hidden rounded-xl border border-line">
      <ThreeColumnLayout
        mobileView={active ? 'main' : 'list'}
        listLabel="Conversaciones"
        list={
          <ConversationList label="Conversaciones" count={visible.length} tabs={tabs}>
            {listBody}
          </ConversationList>
        }
        main={
          active ? (
            <div className="flex h-full flex-col">
              <div className="flex flex-col gap-2 border-b border-line bg-surface px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <span className="grid size-8 flex-none place-items-center rounded-full bg-field text-[11px] font-bold text-text-2">
                    {active.contact.initials}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[13px] font-bold text-text">{active.contact.name}</span>
                    <span className="text-[10.5px] text-text-3">
                      {/* The real channel, not a literal. This said "WhatsApp"
                          for every thread, so an Instagram conversation was
                          labelled as WhatsApp to the agent replying to it. */}
                      {active.channelLabel ? `${active.channelLabel} · ` : ''}
                      {stateLine}
                    </span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* CP-1 F-C: "Tomar conversación" appears ONLY when nobody
                      holds it. It used to appear on a colleague's thread too
                      (they collapsed into one state), so one click silently
                      took somebody else's work — no name picked, no notice to
                      them. Taking over a held thread goes through Reasignar
                      below: you choose the person (yourself included) and the
                      new holder is notified. Same endpoint, same permission,
                      one deliberate gesture instead of a stray click. */}
                  {active.assignment === 'unassigned' && ownUserId ? (
                    <Button size="sm" onClick={onTake}>
                      Tomar conversación
                    </Button>
                  ) : null}
                  {active.assignment === 'you' ? (
                    <Button variant="secondary" size="sm" onClick={onReturnToPool}>
                      Devolver a la cola
                    </Button>
                  ) : null}
                  {/* Solo mode: with only yourself on the team there is nowhere
                      to reassign to, so the affordance doesn't render.

                      CP-1 F-C: and it is "Reasignar" only when there is
                      something to RE-assign. A conversation the Agente is
                      still handling, or one nobody has taken, has never been
                      assigned to anybody — calling that "Reasignar" told the
                      reader a handoff had already happened. */}
                  {canReassign ? (
                    <Button variant="secondary" size="sm" onClick={() => setReassignOpen(true)}>
                      {assignVerb}
                    </Button>
                  ) : null}
                  <Button variant="secondary" size="sm" onClick={onReturnToBot}>
                    Devolver al agente
                  </Button>
                  <Button variant="secondary" size="sm" onClick={onResolve}>
                    Resolver
                  </Button>
                </div>
              </div>

              <div
                tabIndex={0}
                aria-label={`Conversación con ${active.contact.name}`}
                className="flex-1 overflow-y-auto px-4 py-3 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue"
              >
                <div className="mb-3">
                  <ServiceWindowBanner hoursLeft={active.windowHoursLeft} />
                </div>
                <MessageThread label={`Conversación con ${active.contact.name}`}>
                  {active.thread.map((item) => (
                    <ThreadItemView key={item.id} item={item} />
                  ))}
                </MessageThread>
              </div>

              <Composer
                mode={mode}
                onModeChange={setMode}
                onSend={onSend}
                windowOpen={active.windowHoursLeft > 0}
                recipientName={active.contact.name.split(' ')[0] ?? ''}
              />
            </div>
          ) : (
            <div className="grid h-full place-items-center text-[12.5px] text-text-3">
              Selecciona una conversación.
            </div>
          )
        }
        contact={
          active ? (
            <InboxContactPanel
              contact={active.contact}
              nameHref={active.contact.id ? `/contactos/${active.contact.id}` : undefined}
              sections={active.sections}
              tags={active.tags}
              notes={active.notes}
            />
          ) : (
            <div />
          )
        }
      />

      <Modal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        title={`${assignVerb} conversación`}
        subtitle="Pasa esta conversación a otra persona del equipo."
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setReassignOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" disabled={!reassignTo} onClick={onReassign}>
              {assignVerb}
            </Button>
          </>
        }
      >
        <FormField label="Asignar a" htmlFor="inbox-reassign">
          <Select
            id="inbox-reassign"
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
            options={[
              { value: '', label: 'Elige a una persona…' },
              ...assignables.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
        </FormField>
      </Modal>
    </div>
  )
}
